import SideBlockAds from "@components/Blocks/Container/SideBlockAds";
import { getGTMData, getMetaData } from "@helper/getMetaData";
import { Route } from "@route-types/Author/+types/AuthorPage";
import BHBasePage, { BHBasePageProps } from "@routes/BHBasePage";
import ErrorPage from "@routes/ErrorPage";
import NotFound from "@routes/NotFound";
import type {
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import {
  type AuthorPayloadResponseType,
  fetchAuthorData,
} from "./Author.server";
import AuthorMetaData from "./AuthorMetaData";
import AuthorStoriesList from "./AuthorStoriesList";

export function meta({
  loaderData,
}: {
  loaderData: TRouteWithoutRedirect<AuthorPayloadResponseType, string>;
}) {
  const payload = loaderData.payload;
  if (typeof payload !== "object" || !("data" in payload)) return [];
  const authorAlias = payload.authorAlias;
  const authorName = payload.data[0]?.authors?.[0]?.name;
  return [getMetaData(AuthorMetaData(authorAlias, authorName).metaTags)];
}

export default function AuthorPage() {
  const dataLoaderResponse: TRouteWithoutRedirect<
    AuthorPayloadResponseType,
    string
  > = useLoaderData();
  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }

  const articles = dataLoaderResponse.payload.data;
  const authorAlias = dataLoaderResponse.payload.authorAlias;

  let author = (articles[0].authors || [])[0];

  for (const auth of articles[0].authors || []) {
    if (auth.urlPath?.toLowerCase().includes(authorAlias.toLowerCase())) {
      author = auth;
      break;
    }
  }

  const sectionName = "author";
  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;
  const basePageProps: BHBasePageProps = {
    uniqueSectionName: sectionName,
    pageAdTargetValue: pageAdTargetValue,
    hideNavigationInMobile: true,
  };

  return (
    <BHBasePage {...basePageProps}>
      <div className="max-w-screen-cxl m-auto flex flex-col lg:flex-row w-full gap-4">
        <AuthorStoriesList
          articles={articles}
          author={author}
          authorAlias={authorAlias}
          sectionName={sectionName}
        />
        <SideBlockAds
          adsSectionName={sectionName}
          adsOnly={true}
          pageAdTargetingProps={pageAdTargetValue}
          className="flex-1"
        />
      </div>
    </BHBasePage>
  );
}

export async function loader({ params }: Route.LoaderArgs) {
  const authorSlug = params.level1 || "";
  const authorAlias = `/author/${authorSlug}`;
  const data = await fetchAuthorData(authorAlias);
  let authorDisplayName = authorSlug.replace(/-/g, " ");
  if (data.type === ResponseType.SUCCESS) {
    authorDisplayName =
      data.payload.data[0]?.authors?.[0]?.name || authorDisplayName;
  }
  const gtmData = getGTMData(
    AuthorMetaData(authorAlias, authorDisplayName).gaData,
  );

  return {
    ...data,
    gtm: gtmData,
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<AuthorPayloadResponseType, string>
  >;
}
